// features/workstations/qqq/network/index.js
// ═══════════════════════════════════════════════════════════
// NETWORK tab — Obsidian-style mesh of the prediction system.
//
// Nodes:    shapes, actors, signals, variables, days, recommendations
// Edges:    actor→signal, signal→shape, variable→signal, day→shape
// Click:    side panel with node details + connection list
// Hover:    highlight node + direct edges (cascade preview)
//
// v1 scope: read-only graph from committed data/qqq/*.json.
// v2 (later): live prediction nodes + scores from forward-walk grades.
// v3 (later): cascade simulator (what-if).
// ═══════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactFlow, {
  Background, Controls, MiniMap,
  useNodesState, useEdgesState, MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';

// Color by category
const NODE_COLOR = {
  shape: '#c8a96e',         // gold — outcomes
  actor: '#7a9e82',         // sage — agents
  signal: '#b8956a',        // sand — derived signals
  variable: '#6b8fa8',      // steel — inputs
  day: '#9b8fc4',           // purple — observations
  recommendation: '#4db8b8', // teal — decisions
  finding: '#c46050',       // terracotta — research
};

const CATEGORY_LABEL = {
  shape: 'Shape (5)',
  actor: 'Actor (8)',
  signal: 'Signal',
  variable: 'Variable',
  day: 'Day',
  recommendation: 'Recommendation',
};

// Position nodes by category in circular clusters
function layoutNodes(rawNodes) {
  const byType = {};
  rawNodes.forEach((n) => {
    byType[n.type] = byType[n.type] || [];
    byType[n.type].push(n);
  });

  // Center positions per type
  const centers = {
    shape: { x: 0, y: 0 },
    actor: { x: -500, y: -300 },
    signal: { x: -700, y: 100 },
    variable: { x: -350, y: 350 },
    day: { x: 500, y: 0 },
    recommendation: { x: 400, y: -350 },
  };

  const positioned = [];
  Object.entries(byType).forEach(([type, items]) => {
    const center = centers[type] || { x: 0, y: 0 };
    const radius = Math.min(80 + items.length * 18, 220);
    items.forEach((n, i) => {
      const angle = (i / items.length) * Math.PI * 2;
      positioned.push({
        id: n.id,
        type: 'default',
        position: {
          x: center.x + Math.cos(angle) * radius,
          y: center.y + Math.sin(angle) * radius,
        },
        data: { label: n.label, raw: n },
        style: {
          background: '#111118',
          border: `1px solid ${NODE_COLOR[n.type] || '#888'}66`,
          color: NODE_COLOR[n.type] || '#ccc',
          fontSize: 11,
          padding: 6,
          borderRadius: 6,
          width: 'auto',
          minWidth: 60,
        },
      });
    });
  });
  return positioned;
}

function formatEdges(rawEdges) {
  return rawEdges.map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    label: e.label,
    type: 'default',
    style: {
      stroke: '#363650',
      strokeWidth: e.weight ? Math.max(0.5, e.weight * 2) : 1,
    },
    labelStyle: { fill: '#888', fontSize: 9 },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#363650', width: 12, height: 12 },
  }));
}

function NodeDetailsPanel({ node, allNodes, allEdges, onClose }) {
  if (!node) return null;
  const raw = node.data?.raw || {};

  // Connected nodes
  const incoming = allEdges.filter((e) => e.target === node.id);
  const outgoing = allEdges.filter((e) => e.source === node.id);
  const nodeById = Object.fromEntries(allNodes.map((n) => [n.id, n]));

  return (
    <aside className="qqq-network-details">
      <div className="qqq-network-details__header">
        <span className="qqq-network-details__type" style={{ color: NODE_COLOR[raw.type] }}>
          {raw.type?.toUpperCase()}
        </span>
        <button onClick={onClose}>✕</button>
      </div>
      <h3>{raw.label}</h3>
      {raw.desc && <p className="qqq-network-details__desc">{raw.desc}</p>}

      {raw.sampleSize != null && (
        <div className="qqq-network-details__stat">
          <span>Sample size:</span>
          <strong>{raw.sampleSize}</strong>
        </div>
      )}
      {raw.baseRatePct != null && (
        <div className="qqq-network-details__stat">
          <span>Base rate:</span>
          <strong>{raw.baseRatePct.toFixed(1)}%</strong>
        </div>
      )}
      {raw.accuracy != null && (
        <div className="qqq-network-details__stat">
          <span>Top-1 hit rate:</span>
          <strong>{raw.accuracy.toFixed(1)}%</strong>
        </div>
      )}
      {raw.hitRate != null && (
        <div className="qqq-network-details__stat">
          <span>Signal hit rate:</span>
          <strong>{raw.hitRate.toFixed(0)}%</strong>
        </div>
      )}
      {raw.edge && (
        <div className="qqq-network-details__stat">
          <span>Edge type:</span>
          <strong>{raw.edge}</strong>
        </div>
      )}
      {raw.fullText && (
        <div className="qqq-network-details__signal">
          <span>Signal:</span>
          <code>{raw.fullText}</code>
        </div>
      )}
      {raw.topOutcome && (
        <div className="qqq-network-details__stat">
          <span>Top outcome:</span>
          <strong>{raw.topOutcome} ({raw.topOutcomeCount}/{raw.occurrences})</strong>
        </div>
      )}

      <div className="qqq-network-details__conns">
        <h4>Connections ({incoming.length + outgoing.length})</h4>
        {outgoing.length > 0 && (
          <>
            <div className="qqq-network-details__conn-label">→ Outgoing ({outgoing.length})</div>
            <ul>
              {outgoing.map((e) => {
                const target = nodeById[e.target];
                return (
                  <li key={e.id}>
                    <span className="qqq-network-details__conn-type">{e.label || 'link'}</span>
                    <span style={{ color: NODE_COLOR[target?.data?.raw?.type] || '#888' }}>
                      {target?.data?.label || e.target}
                    </span>
                  </li>
                );
              })}
            </ul>
          </>
        )}
        {incoming.length > 0 && (
          <>
            <div className="qqq-network-details__conn-label">← Incoming ({incoming.length})</div>
            <ul>
              {incoming.map((e) => {
                const source = nodeById[e.source];
                return (
                  <li key={e.id}>
                    <span style={{ color: NODE_COLOR[source?.data?.raw?.type] || '#888' }}>
                      {source?.data?.label || e.source}
                    </span>
                    <span className="qqq-network-details__conn-type">{e.label || 'link'}</span>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </aside>
  );
}

function Legend({ activeFilter, onFilter }) {
  const categories = Object.keys(NODE_COLOR);
  return (
    <div className="qqq-network-legend">
      <span style={{ opacity: 0.6, fontSize: 10, marginRight: 8 }}>FILTER:</span>
      {categories.map((cat) => (
        <button
          key={cat}
          onClick={() => onFilter(cat)}
          style={{
            background: activeFilter === cat ? `${NODE_COLOR[cat]}22` : 'transparent',
            border: `1px solid ${NODE_COLOR[cat]}66`,
            color: NODE_COLOR[cat],
            padding: '4px 8px',
            borderRadius: 4,
            fontSize: 10,
            cursor: 'pointer',
            marginRight: 6,
          }}
        >
          {cat}
        </button>
      ))}
      <button
        onClick={() => onFilter(null)}
        style={{
          background: !activeFilter ? '#22223a' : 'transparent',
          border: '1px solid #363650',
          color: '#ccc',
          padding: '4px 8px',
          borderRadius: 4,
          fontSize: 10,
          cursor: 'pointer',
        }}
      >
        all
      </button>
    </div>
  );
}

export default function QqqNetworkTab() {
  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/qqq/brain-graph');
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setGraph(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const { positioned, formattedEdges } = useMemo(() => {
    if (!graph) return { positioned: [], formattedEdges: [] };
    const filteredNodes = filter ? graph.nodes.filter((n) => n.type === filter) : graph.nodes;
    const nodeIds = new Set(filteredNodes.map((n) => n.id));
    const filteredEdges = graph.edges.filter((e) => nodeIds.has(e.source) && nodeIds.has(e.target));
    return {
      positioned: layoutNodes(filteredNodes),
      formattedEdges: formatEdges(filteredEdges),
    };
  }, [graph, filter]);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  useEffect(() => { setNodes(positioned); }, [positioned, setNodes]);
  useEffect(() => { setEdges(formattedEdges); }, [formattedEdges, setEdges]);

  const onNodeClick = useCallback((_event, node) => {
    setSelectedNode(node);
  }, []);

  return (
    <div className="qqq-network-tab">
      <style jsx>{`
        .qqq-network-tab {
          display: flex;
          flex-direction: column;
          height: calc(100vh - 200px);
          min-height: 600px;
          padding: 8px;
          color: #ccc;
          position: relative;
        }
        .qqq-network-header {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 8px 12px;
          background: #0c0c11;
          border: 1px solid #1e1e2a;
          border-radius: 8px;
          margin-bottom: 8px;
        }
        .qqq-network-header h2 {
          margin: 0;
          font-size: 14px;
          color: #c8a96e;
        }
        .qqq-network-header__meta {
          font-size: 11px;
          color: #888;
        }
        .qqq-network-graph {
          flex: 1;
          background: #07070a;
          border: 1px solid #1e1e2a;
          border-radius: 8px;
          position: relative;
          overflow: hidden;
        }
        :global(.qqq-network-legend) {
          display: flex;
          align-items: center;
          padding: 8px 12px;
          background: #0c0c11;
          border: 1px solid #1e1e2a;
          border-radius: 8px;
          margin-bottom: 8px;
          flex-wrap: wrap;
        }
        :global(.qqq-network-details) {
          position: absolute;
          right: 16px;
          top: 16px;
          width: 320px;
          max-height: calc(100% - 32px);
          background: #0c0c11;
          border: 1px solid #272736;
          border-radius: 8px;
          padding: 16px;
          overflow-y: auto;
          z-index: 10;
          box-shadow: -4px 4px 24px rgba(0,0,0,0.5);
        }
        :global(.qqq-network-details__header) {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }
        :global(.qqq-network-details__type) {
          font-size: 10px;
          letter-spacing: 1px;
          font-weight: 600;
        }
        :global(.qqq-network-details__header button) {
          background: transparent;
          color: #888;
          border: none;
          cursor: pointer;
          font-size: 14px;
        }
        :global(.qqq-network-details h3) {
          margin: 0 0 8px;
          font-size: 14px;
          color: #ccc;
        }
        :global(.qqq-network-details__desc) {
          font-size: 11px;
          color: #888;
          line-height: 1.5;
          margin: 0 0 12px;
        }
        :global(.qqq-network-details__stat) {
          display: flex;
          justify-content: space-between;
          padding: 4px 0;
          font-size: 11px;
          color: #aaa;
          border-bottom: 1px solid #15151e;
        }
        :global(.qqq-network-details__stat strong) {
          color: #c8a96e;
          font-weight: 600;
        }
        :global(.qqq-network-details__signal) {
          margin: 8px 0;
        }
        :global(.qqq-network-details__signal span) {
          font-size: 10px;
          color: #888;
          letter-spacing: 0.5px;
        }
        :global(.qqq-network-details__signal code) {
          display: block;
          background: #171723;
          color: #b8956a;
          padding: 6px 8px;
          border-radius: 4px;
          font-size: 11px;
          margin-top: 4px;
          font-family: ui-monospace, monospace;
        }
        :global(.qqq-network-details__conns) {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid #272736;
        }
        :global(.qqq-network-details__conns h4) {
          margin: 0 0 8px;
          font-size: 11px;
          color: #888;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        :global(.qqq-network-details__conn-label) {
          font-size: 10px;
          color: #666;
          margin: 8px 0 4px;
        }
        :global(.qqq-network-details__conns ul) {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        :global(.qqq-network-details__conns li) {
          padding: 3px 0;
          font-size: 11px;
          display: flex;
          gap: 6px;
          align-items: center;
        }
        :global(.qqq-network-details__conn-type) {
          color: #555;
          font-size: 9px;
          background: #15151e;
          padding: 1px 4px;
          border-radius: 2px;
        }
      `}</style>

      <div className="qqq-network-header">
        <h2>Knowledge Network</h2>
        {graph?.meta && (
          <span className="qqq-network-header__meta">
            {graph.meta.totalDays} days · {graph.meta.actorsLoaded} actors · {graph.meta.signalsLoaded} signals · {graph.meta.recentDaysShown} recent days shown
          </span>
        )}
        <Legend activeFilter={filter} onFilter={setFilter} />
      </div>

      <div className="qqq-network-graph">
        {loading && <p style={{ padding: 20, color: '#888' }}>Loading graph…</p>}
        {error && <p style={{ padding: 20, color: '#c46050' }}>Error: {error}</p>}
        {graph && (
          <>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onNodeClick={onNodeClick}
              fitView
              minZoom={0.2}
              maxZoom={1.5}
              proOptions={{ hideAttribution: true }}
            >
              <Background color="#1e1e2a" gap={20} />
              <Controls
                style={{
                  background: '#111118',
                  border: '1px solid #272736',
                }}
              />
              <MiniMap
                nodeColor={(n) => NODE_COLOR[n.data?.raw?.type] || '#888'}
                style={{
                  background: '#07070a',
                  border: '1px solid #272736',
                }}
              />
            </ReactFlow>
            <NodeDetailsPanel
              node={selectedNode}
              allNodes={nodes}
              allEdges={edges}
              onClose={() => setSelectedNode(null)}
            />
          </>
        )}
      </div>
    </div>
  );
}
